import { Save, X } from "lucide-react";
import { useState } from "react";

interface Sector {
  id: string;
  name: string;
}

interface CompanyFormProps {
  sectors: Sector[];
  initial?: {
    name: string;
    relevance: string;
    employeeCountRange: string;
    website: string;
    notes: string;
    targetSectorId: string;
  };
  onSave: (data: {
    name: string;
    relevance: string;
    employeeCountRange: string;
    website: string;
    notes: string;
    targetSectorId: string;
  }) => void;
  onCancel: () => void;
  saving: boolean;
}

export function CompanyForm({ sectors, initial, onSave, onCancel, saving }: CompanyFormProps) {
  const [name, setName] = useState(initial?.name ?? "");
  const [relevance, setRelevance] = useState(initial?.relevance ?? "medium");
  const [employeeCountRange, setEmployeeCountRange] = useState(initial?.employeeCountRange ?? "");
  const [website, setWebsite] = useState(initial?.website ?? "");
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [targetSectorId, setTargetSectorId] = useState(initial?.targetSectorId ?? "");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSave({
      name: name.trim(),
      relevance,
      employeeCountRange,
      website,
      notes,
      targetSectorId,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3 rounded border border-border p-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-text-secondary">Company Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            placeholder="e.g. Barclays"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-text-secondary">Relevance</label>
          <select
            value={relevance}
            onChange={(e) => setRelevance(e.target.value)}
            className="mt-1 w-full rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
          >
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-text-secondary">Sector</label>
          <select
            value={targetSectorId}
            onChange={(e) => setTargetSectorId(e.target.value)}
            className="mt-1 w-full rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
          >
            <option value="">No sector</option>
            {sectors.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-text-secondary">Employees</label>
          <input
            type="text"
            value={employeeCountRange}
            onChange={(e) => setEmployeeCountRange(e.target.value)}
            className="mt-1 w-full rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            placeholder="e.g. 1000-5000"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-text-secondary">Website</label>
          <input
            type="text"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            className="mt-1 w-full rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            placeholder="e.g. barclays.com"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-text-secondary">Notes</label>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="mt-1 w-full rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            placeholder="Optional notes"
          />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex items-center gap-1 rounded px-2 py-1 text-xs text-text-muted hover:bg-card-hover"
        >
          <X size={12} /> Cancel
        </button>
        <button
          type="submit"
          disabled={saving || !name.trim()}
          className="flex items-center gap-1 rounded bg-accent px-3 py-1 text-xs font-medium text-white hover:bg-accent/90 disabled:opacity-50"
        >
          <Save size={12} /> {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </form>
  );
}
